import { Trans, useTranslation } from '@e-kyc/translations';

import {
  Button,
  LoadingSpinner,
  Modal,
  WarningRoundedOutlinedIcon,
} from '../../../../../../components';
import { type EkycStep } from '../../../../../../providers';

export type DopaModalVariant = 'success' | 'invalid' | 'expired' | 'unknown';

interface IdCardConfirmationDopaModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  setStep: (step: EkycStep) => void;
  variant?: DopaModalVariant;
  className?: string;
}

interface ModalContentProps {
  variant: DopaModalVariant;
  onClose: () => void;
  setStep: (step: EkycStep) => void;
}

const ModalContent = ({ variant, onClose, setStep }: Readonly<ModalContentProps>) => {
  const { t } = useTranslation();

  const variants = {
    success: (
      <div className='flex w-full flex-col items-center justify-center gap-4 p-6'>
        <img
          src='id_back_passed.png'
          alt='back id passed icon'
          className='h-16 w-16 object-contain'
        />
        <div className='flex flex-col items-center justify-center gap-2'>
          <h5 className='font-semibold'>{t('idCardDetails.modal.dopa.title')}</h5>
          <div className='text-body1 text-text-secondary text-center'>
            {t('idCardDetails.modal.dopa.description')}
          </div>
        </div>
        <Button className='w-full' onClick={() => setStep('capture-face-consent')}>
          {t('button.next')}
        </Button>
      </div>
    ),
    invalid: (
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='flex flex-col items-center justify-center gap-4 p-6'>
          <WarningRoundedOutlinedIcon className='text-warning-500 size-12' />
          <div className='flex flex-col gap-2'>
            <h5 className='font-semibold'>{t('idCardDetails.modal.dopa.wrongInput.title')}</h5>
            <div className='text-body1 text-text-secondary'>
              <Trans i18nKey='idCardDetails.modal.dopa.wrongInput.description' />
              <div className='pl-2'>
                <Trans i18nKey='idCardDetails.modal.dopa.wrongInput.bullet' />
              </div>
            </div>
          </div>
        </div>
        <div className='w-full p-4'>
          <Button className='w-full' onClick={onClose}>
            {t('button.retry')}
          </Button>
        </div>
      </div>
    ),
    expired: (
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='flex flex-col items-center justify-center gap-4 p-6'>
          <WarningRoundedOutlinedIcon className='text-warning-500 size-12' />
          <div className='flex flex-col gap-2 text-center'>
            <h5 className='font-semibold'>{t('idCardDetails.modal.dopa.expired.title')}</h5>
            <div className='text-body1 text-text-secondary'>
              <Trans i18nKey='idCardDetails.modal.dopa.expired.description' />
            </div>
          </div>
        </div>
        <div className='w-full p-4'>
          <Button className='w-full' onClick={onClose}>
            {t('button.acknowledge')}
          </Button>
        </div>
      </div>
    ),
    unknown: (
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='flex flex-col items-center justify-center gap-4 p-6'>
          <WarningRoundedOutlinedIcon className='text-warning-500 size-12' />
          <div className='flex flex-col gap-2 text-center'>
            <h5 className='font-semibold'>
              <Trans i18nKey='idCardDetails.modal.dopa.unknown.title' />
            </h5>
            <div className='text-body1 text-text-secondary'>
              <Trans i18nKey='idCardDetails.modal.dopa.unknown.description' />
            </div>
          </div>
        </div>
        <div className='w-full p-4'>
          <Button className='w-full' onClick={onClose}>
            {t('button.retry')}
          </Button>
        </div>
      </div>
    ),
  };

  return variants[variant];
};

function IdCardConfirmationDopaModal(props: Readonly<IdCardConfirmationDopaModalProps>) {
  const {
    isOpen,
    isLoading,
    onClose,
    setStep,
    variant = 'success',
    className = 'w-full max-w-[343px]',
  } = props;

  const { t } = useTranslation();

  return (
    <Modal open={isOpen} className={className}>
      {isLoading ? (
        <div className='flex w-full flex-col items-center justify-center gap-4 py-8'>
          <div className='flex size-12 flex-col items-center justify-center'>
            <LoadingSpinner />
          </div>
          <h5>{t('idCardDetails.modal.loading')}</h5>
        </div>
      ) : (
        <ModalContent variant={variant} onClose={onClose} setStep={setStep} />
      )}
    </Modal>
  );
}

export default IdCardConfirmationDopaModal;
